import axios from 'axios';
import { getActiveSystem } from './system';
import { getActiveStack } from './stack';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  emitAuthLogout,
} from './authStore';

// In Docker, always use the environment variable
// In development, fall back to relative URLs for proxy
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Tag every request with the active game system via a query param. The backend
// scopes all content on ?system= (or an X-Game-System header). We use the query
// param deliberately: a custom header makes every cross-origin GET a non-simple
// request, forcing a CORS preflight that nginx answers WITHOUT allowing
// X-Game-System — which blocks every call. A query param keeps requests simple.
// An explicit per-request system param (e.g. a deep link) is respected.
apiClient.interceptors.request.use((config) => {
  config.params = config.params || {};
  if (config.params.system == null) {
    config.params.system = getActiveSystem();
  }
  // The active homebrew stack rides the same way and for the same reasons
  // (MRCS Phase B). It is added ONLY when one is active — with no stack the
  // request is the request it was, which is what the invariant in 09 §2 asks
  // for — and the API serves base content to a caller who names a stack they
  // do not own, so a stale value cannot expose anything.
  if (config.params.stack == null) {
    const stack = getActiveStack();
    if (stack) config.params.stack = stack;
  }
  return config;
});

// Attach the access token when logged in. Authorization is NOT a custom header
// pitfall like X-Game-System: nginx's Access-Control-Allow-Headers already
// lists Authorization in both api*.thedragonstable.com confs, so the CORS
// preflight it triggers is answered correctly.
//
// A token known to have run out is refreshed first (qa-runs#144). A public
// route reads an expired token as nobody's rather than answering 401, so the
// systems list came back without the reader's homebrew and the page fell back
// to the default system. A token that cannot be read goes as it is, and the
// 401 path below still covers it.
const withToken = (config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};
const hasRunOut = (token) => {
  try {
    const part = token.split('.')[1];
    const { exp } = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp === 'number' && exp * 1000 <= Date.now();
  } catch {
    return false;
  }
};
apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (!token || !getRefreshToken() || isAuthPath(config.url) || !hasRunOut(token)) return withToken(config);
  const had = getRefreshToken();
  return refreshTokens()
    .catch(() => {
      // The same end as a failed refresh after a 401, unless someone signed in meanwhile.
      if (getRefreshToken() === had) { clearTokens(); emitAuthLogout(); }
    })
    .then(() => withToken(config));
});

// The auth endpoints must never trigger a token refresh: a 401 from /login is
// "wrong password", and a 401 from /refresh means the refresh token itself is
// dead — retrying either would loop.
const AUTH_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/auth/google',
  '/api/auth/logout',
];
const isAuthPath = (url = '') => AUTH_PATHS.some((p) => url.includes(p));

// Single-flight refresh: concurrent 401s share one /api/auth/refresh call.
// Refresh tokens rotate server-side on every use, so a second parallel refresh
// with the same (now-revoked) token would 401 and force a spurious logout.
//
// A sign-in while a refresh is in flight (qa-runs#153: a test-login link opened
// in a browser signed in as someone else) replaces the session: the refresh's
// answer is then the old account's and is dropped, never written over the new
// one. The request retried after it carries the new session's token.
let refreshPromise = null;
const refreshTokens = () => {
  if (!refreshPromise) {
    const sent = getRefreshToken();
    refreshPromise = apiClient
      .post('/api/auth/refresh', { refreshToken: sent })
      .then((res) => {
        if (getRefreshToken() === sent) setTokens(res.data);
        return res.data;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Error handling + one-shot 401 recovery: on a 401 from a non-auth endpoint,
// refresh the access token once and retry the original request once
// (config._authRetry guards the loop). If the refresh itself fails the session
// is dead: clear tokens and broadcast a logout so AuthContext drops the user.
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const config = error.config;
    if (
      error.response?.status === 401 &&
      config &&
      !config._authRetry &&
      !isAuthPath(config.url) &&
      getRefreshToken()
    ) {
      config._authRetry = true;
      const had = getRefreshToken();
      try {
        await refreshTokens();
      } catch (refreshError) {
        // The old session's refresh failing says nothing about one signed in since (qa-runs#153): retry as that one.
        if (getRefreshToken() !== had) return apiClient(config);
        clearTokens();
        emitAuthLogout();
        return Promise.reject(error);
      }
      // The request interceptor re-reads the (now fresh) access token.
      return apiClient(config);
    }
    console.error('API Error:', error.response?.status, error.config?.url, error.message);
    return Promise.reject(error);
  }
);

export default apiClient;
